import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, HStack, VStack, Text, Input, Button } from "@chakra-ui/react";
import { FaRobot } from "react-icons/fa";
import { getchatBot } from "../../actions/user/userActions";

const Chatbot = () => {
  const [messages, setMessages] = useState([
    { text: "Hello, how can I help you today?", sender: "bot" },
  ]);
  const [input, setInput] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const dispatch = useDispatch();

  const { data: userData } = useSelector((state) => state.userReducer);

  const {
    loading: chatLoading,
    message: chatMsg,
    data: chatData,
  } = useSelector((state) => state.chatbotReducer);

  const handleSend = useCallback(async () => {
    if (!input.trim()) return;

    if (!userData?.id) {
      return;
    }

    const userMessage = { text: input, sender: "user" };
    setInput("");
    setMessages((prev) => [...prev, userMessage]);

    dispatch(getchatBot(input, userData?.id));
  }, [input, userData?.id, dispatch]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSend();
      }
    },
    [handleSend]
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!chatLoading) {
      setMessages((prev) => [
        ...prev,
        { text: chatData ? chatData : chatMsg, sender: "bot" },
      ]);
    }
  }, [chatLoading]);

  // Drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Minimize/Maximize functionality
  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (isMaximized) setIsMaximized(false);
  };

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    if (isMinimized) setIsMinimized(false);
  };

  // Close functionality
  const handleClose = () => {
    setIsVisible(false);
    if (typeof window !== "undefined") {
      localStorage.setItem("chatbotVisibility", JSON.stringify(false));
    }
    console.log("Chatbot closed and state saved");
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  if (!isVisible) return null;

  return (
    <Box
      ref={chatBoxRef}
      position="absolute"
      right="20px"
      w={isMaximized ? "90vw" : "400px"}
      h={isMinimized ? "40px" : isMaximized ? "90vh" : "500px"}
      p={0}
      borderRadius="lg"
      boxShadow="md"
      bg="#F8F8F8"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      border="1px solid #ccc"
      zIndex="1000"
      transform={`translate(${position.x}px, ${position.y}px)`}
      style={{
        transition: isDragging ? "none" : "transform 0.2s ease",
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Chat header */}
      <HStack
        bg="#E6E6E6"
        p={2}
        borderTopRadius="lg"
        justifyContent="space-between"
        cursor={isDragging ? "grabbing" : "move"}
        onMouseDown={handleMouseDown}
      >
        <HStack>
          <Box mr={2} cursor="move">
            <svg
              width="25"
              height="25"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 4V6M8 10V12M4 8H6M10 8H12"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path d="M8 3L10 5L6 5L8 3Z" fill="black" />
              <path d="M8 13L6 11L10 11L8 13Z" fill="black" />
              <path d="M3 8L5 10L5 6L3 8Z" fill="black" />
              <path d="M13 8L11 6L11 10L13 8Z" fill="black" />
            </svg>
          </Box>
          <FaRobot color="black" />
          <Text fontWeight="bold">Hello {userData?.full_name || "User"}</Text>
        </HStack>

        <HStack spacing={3}>
          {/* Minimize button */}
          <Box
            onClick={handleMinimize}
            cursor="pointer"
            _hover={{ bg: "#D6D6D6" }}
            p={1}
            borderRadius="sm"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="2" y="5" width="8" height="2" fill="black" />
            </svg>
          </Box>
          <Box
            onClick={handleMaximize}
            cursor="pointer"
            _hover={{ bg: "#D6D6D6" }}
            p={1}
            borderRadius="sm"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect
                x="1"
                y="1"
                width="10"
                height="10"
                stroke="black"
                strokeWidth="2"
                fill="none"
              />
            </svg>
          </Box>

          {/* Close button */}
          <Box
            onClick={handleClose}
            cursor="pointer"
            _hover={{ bg: "#D6D6D6" }}
            p={1}
            borderRadius="sm"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M1 11L11 1" stroke="black" strokeWidth="2" />
            </svg>
          </Box>
        </HStack>
      </HStack>

      {!isMinimized && (
        <>
          <VStack
            spacing={3}
            align="stretch"
            overflowY="auto"
            flex="1"
            p={2}
            maxH={isMaximized ? "calc(90vh - 100px)" : "calc(500px - 100px)"}
          >
            {messages.map((msg, index) => (
              <Box
                key={`${index}-${msg.text ? msg.text.substring(0, 10) : ""}`}
                alignSelf={msg.sender === "user" ? "flex-end" : "flex-start"}
                bg={msg.sender === "user" ? "blue.500" : "gray.200"}
                color={msg.sender === "user" ? "white" : "black"}
                px={4}
                py={2}
                borderRadius="md"
                maxW="80%"
              >
                <Text>{msg.text}</Text>
              </Box>
            ))}

            {chatLoading && (
              <Box
                alignSelf="flex-start"
                bg="gray.200"
                px={4}
                py={2}
                borderRadius="md"
                maxW="80%"
              >
                <Text>Thinking...</Text>
              </Box>
            )}
            <div ref={chatEndRef} />
          </VStack>

          {/* Input area */}
          <HStack mt={2} p={2}>
            <Input
              placeholder="Type your query here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={chatLoading}
            />
            <Button
              colorScheme="blue"
              onClick={handleSend}
              isLoading={chatLoading}
              loadingText="Sending..."
              disabled={!input.trim()}
            >
              Send
            </Button>
          </HStack>

          {/* Action buttons */}
          <HStack mt={2} p={2} justifyContent="center">
            <Button
              size="sm"
              w="215px"
              bg="#5D5D5D"
              color="white"
              fontSize="12px"
              fontWeight="normal"
              _hover={{ bg: "#4B4B4B" }}
              onClick={() => setInput("Generate competitive analysis")}
            >
              Generate competitive analysis
            </Button>
            <Button
              size="sm"
              w="192px"
              bg="#5D5D5D"
              color="white"
              fontSize="12px"
              fontWeight="normal"
              _hover={{ bg: "#4B4B4B" }}
              onClick={() => setInput("Analyze team performance")}
            >
              Analyze team performance
            </Button>
          </HStack>
        </>
      )}
    </Box>
  );
};

export default React.memo(Chatbot);
